<template>
  <v-stepper v-model="commonStepperStep" vertical>
    <v-stepper-step step="1" editable :rules="[() => schedule_validation]">
      <span v-if="is_vod_event">
        {{ $t("vod.save.page.step.title.availability") }}
        <div>
          <small>{{ $t("vod.save.page.step.title.availability.desc") }}</small>
        </div>
      </span>
      <span v-else>
        {{ $t(`${eventPrefix}.save.step.schedule_screening`) }}
        <div>
          <small>{{
            $t(`${eventPrefix}.save.step.schedule_screening.desc`)
          }}</small>
        </div>
      </span>
    </v-stepper-step>
    <v-stepper-content step="1">
      <v-form ref="schedule_registration">
        <div v-if="is_vod_event" class="pa-4">
          <!-- Screener period -->
          <v-text-field
            id="vod_availability"
            :label="$t('vod.save.form.input.label.availability')"
            name="vod_availability"
            :rules="[rules.required, rules.numeric]"
            outline
            color="primary"
            append-icon="$vuetify.icons.rms_agreement"
            type="number"
            v-model="screener.vod_availability"
          >
          </v-text-field>

          <v-card-actions>
            <v-btn color="secondary" dark @click="goToStep(2)">
              {{ $t(`${eventPrefix}.btn.continue`) }}
              <v-icon small>$vuetify.icons.screener_next</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </div>
        <div v-else class="pa-4">
          <v-layout row wrap>
            <v-flex xs12 sm12 md6>
              <!-- Screener period -->
              <v-layout column mb-3>
                <v-flex xs12 md6 lg3>
                  <DateTimePicker
                    key="screening_starts_at"
                    :label="$t(`${eventPrefix}.save.form.starts_at`)"
                    datetime="screening_starts_at"
                    v-model="screener.screening_starts_at"
                    :hint="
                      $t(
                        `${eventPrefix}.monetization.paid_screening.edit_warning`
                      )
                    "
                  />

                  <!-- Showing local time regarding company configured timezone -->
                  <ShowLocalTime
                    v-if="screener.screening_starts_at"
                    key="screening_starts_at_local"
                    :datetime="screener.screening_starts_at"
                    :timezone="timezone"
                  ></ShowLocalTime>
                </v-flex>
                <v-flex xs12 md6 lg3>
                  <DateTimePicker
                    key="screening_ends_at"
                    :label="$t(`${eventPrefix}.save.form.ends_at`)"
                    datetime="screening_ends_at"
                    v-model="screener.screening_ends_at"
                  />

                  <!-- Showing local time regarding company configured timezone -->
                  <ShowLocalTime
                    v-if="screener.screening_ends_at"
                    key="screening_ends_at_local"
                    :datetime="screener.screening_ends_at"
                    :timezone="timezone"
                  ></ShowLocalTime>
                </v-flex>
                <v-divider class="mb-3" />
                <v-flex xs12>
                  <!-- Start Date -->
                  <DateTimePicker
                    key="registration_starts_at"
                    :label="
                      $t(`${eventPrefix}.save.form.registration_starts_at`)
                    "
                    datetime="registration_starts_at"
                    v-model="screener.registration_starts_at"
                  />

                  <ShowLocalTime
                    v-if="screener.registration_starts_at"
                    key="registration_starts_at_local"
                    :datetime="screener.registration_starts_at"
                    :timezone="timezone"
                  ></ShowLocalTime>
                </v-flex>

                <v-flex xs12>
                  <!-- End Date -->
                  <DateTimePicker
                    key="registration_ends_at"
                    :label="$t(`${eventPrefix}.save.form.registration_ends_at`)"
                    datetime="registration_ends_at"
                    v-model="screener.registration_ends_at"
                  />
                  <ShowLocalTime
                    v-if="screener.registration_ends_at"
                    key="registration_ends_at_local"
                    :datetime="screener.registration_ends_at"
                    :timezone="timezone"
                  ></ShowLocalTime>
                </v-flex>
                <v-divider class="mb-3" />
                <v-flex xs12 md6 lg3 v-if="isPhysicalEvent()">
                  <DateTimePicker
                    key="screener_tolerance"
                    :label="$t(`${eventPrefix}.save.form.screener_tolerance`)"
                    datetime="screening_tolerance"
                    v-model="screener.screening_tolerance"
                    :is_tooltip="true"
                    :tooltip_content="
                      $t(`${eventPrefix}.tolerance_time.definition`)
                    "
                  ></DateTimePicker>

                  <ShowLocalTime
                    v-if="screener.screening_tolerance"
                    key="screener_tolerance_local"
                    :datetime="screener.screening_tolerance"
                    :timezone="timezone"
                  ></ShowLocalTime>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs0 sm0 md1></v-flex>
            <v-flex xs12 sm12 md5>
              <v-card flat>
                <v-card-title class="font-weight-bold grey--text">
                  {{ $t("screener.save.overview.section.screening_timelines") }}
                </v-card-title>
                <v-card-text class="py-0">
                  <v-timeline dense align-top>
                    <v-timeline-item
                      v-for="(timeline, index) in valid_timelines"
                      :key="index + 1"
                      :color="timeline.color"
                      small
                    >
                      <v-layout row wrap pt-2>
                        <v-flex xs12 md12>
                          <strong :class="`${timeline.color}--text`">{{
                            timeline.string
                          }}</strong>
                        </v-flex>
                        <v-flex xs12 md12>
                          <strong>{{
                            moment(timeline.timestamp).format(
                              "dddd, MMMM Do YYYY, h:mm a"
                            )
                          }}</strong>
                        </v-flex>
                      </v-layout>
                    </v-timeline-item>
                  </v-timeline>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>

          <v-card-actions>
            <v-btn color="secondary" dark @click="goToStep(2)">
              {{ $t(`${eventPrefix}.btn.continue`) }}
              <v-icon small>$vuetify.icons.screener_next</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </div>
      </v-form>
    </v-stepper-content>

    <v-stepper-step editable :rules="[() => details_validation]" step="2">
      {{ $t(`${eventPrefix}.save.step.basic_details`) }}
      <small>{{ $t(`${eventPrefix}.save.step.basic_details.desc`) }}</small>
    </v-stepper-step>

    <v-stepper-content step="2">
      <v-form ref="basic_detail">
        <div class="pa-4">
          <v-layout row wrap>
            <v-flex class="mb-2">
              <!-- Title -->
              <v-text-field
                id="title"
                :label="$t(`${eventPrefix}.save.form.screener_title`)"
                name="title"
                outline
                :rules="[
                  rules.required,
                  rules.min_length(3),
                  rules.max_length(250),
                ]"
                counter="250"
                color="primary"
                append-icon="$vuetify.icons.rms_agreement"
                type="text"
                v-model="screener.title"
              ></v-text-field>
              <!-- Organization -->
              <!-- <v-text-field
                outline
                id="venue"
                :label="$t(`${eventPrefix}.save.form.venue`)"
                color="primary"
                name="venue"
                :rules="[rules.min_length(3), rules.max_length(250)]"
                counter="250"
                append-icon="$vuetify.icons.rms_agreement"
                type="text"
                v-model="screener.venue"
              ></v-text-field> -->
              <!-- event type -->
                <!-- <v-select
                  v-if="!is_vod_event"
                  outline
                  v-model="screener.event_type"
                  :items="DEFINES.SCREENER_TYPES"
                  :label="$t('screener.overview.event_type')"
                  item-text="text"
                  item-value="value"
                  hide-details
              ></v-select> -->
            </v-flex>
          </v-layout>
          <v-card-actions>
            <v-btn color="secondary" dark @click="goToStep(3)">
              {{ $t(`${eventPrefix}.btn.continue`) }}
              <v-icon small>$vuetify.icons.screener_next</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </div>
      </v-form>
    </v-stepper-content>

    <v-stepper-step editable step="3" :rules="[() => manage_film_validation]"
      >{{ $t(`${eventPrefix}.save.step.manage_film`) }}
      <small>{{ $t(`${eventPrefix}.save.step.manage_film.desc`) }}</small>
    </v-stepper-step>

    <v-stepper-content step="3">
      <v-form ref="manage_film">
        <div class="pa-4">
          <v-layout row wrap>
            <v-flex>
              <v-select
                v-model="screening_type"
                :items="DEFINES.SCREENING_TYPE_STR"
                item-text="text"
                item-value="value"
                :rules="[]"
                :label="$t(`${eventPrefix}.save.screening_type.label`)"
                outline
                :name="$t(`${eventPrefix}.save.screening_type.label`)"
              ></v-select>
              <AskFilm
              :films="films"
              :film_ids.sync="screener.film_ids"
              ></AskFilm>
            </v-flex>
          </v-layout>
          <div
            v-if="screening_type == DEFINES.SCREENING_TYPE.MULTI_FILM_SCREENING"
          >
            <v-layout column wrap>
              <v-flex xs12 md4 mb-4>
                <div class="pb-2">
                  <span class="subheading greyDarken1--text">{{
                    $t(`${eventPrefix}.save.form.upload_thumbnail`)
                  }}</span>
                  <v-tooltip top color="white">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" size="18" class="px-1"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <div class="greyDarken3--text body-1">
                      {{
                        $t(`${eventPrefix}.save.form.upload_thumbnail.tooltip`)
                      }}
                    </div>
                  </v-tooltip>
                </div>

                <v-container
                  bg
                  fill-height
                  grid-list-md
                  text-xs-center
                  py-0
                  pl-0
                >
                  <v-layout row wrap justify-center align-center>
                    <v-flex pa-0>
                      <v-hover>
                        <v-card
                          @click="showThumbnailUploader = true"
                          height="20vmin"
                          hover
                          slot-scope="{ hover }"
                        >
                          <v-img
                            v-if="screener.thumbnail_url"
                            height="100%"
                            contain
                            :src="screener.thumbnail_url"
                          >
                            <v-expand-transition :key="screener.thumbnail_id">
                              <div
                                v-if="hover"
                                class="d-flex transition-fast-in-fast-out error v-card--reveal white--text text-xs-center"
                                style="height: 15%"
                                @click.stop="removeThumbnail()"
                              >
                                <span class="body-2">Remove</span>
                              </div>
                            </v-expand-transition>
                          </v-img>
                          <v-icon color="blue-grey lighten-4" style="font-size: 20vmin" v-else>mdi-cloud-upload</v-icon>
                        </v-card>
                      </v-hover>
                    </v-flex>
                  </v-layout>
                </v-container>

                <SingleFileUploader
                  v-if="screener"
                  key="logo_uploader"
                  v-model="showThumbnailUploader"
                  :title="$t(`${eventPrefix}.company.profile.upload_logo`)"
                  :warning="$t(`${eventPrefix}.admin.drag_and_drop.warning`)"
                  :attribute="'thumbnail_url'"
                  :collection="this.DEFINES.COLLECTION_PUBLIC"
                  :dropzoneOptions="
                    dropzoneOptionsForThumbnail('upload_thumbnail', screener.id)
                  "
                  @setThumbnal="setScreenerThumbnail"
                  @successUpload="afterThumbnailUpload"
                  @onError="showError"
                  :model_id="screener.id"
                ></SingleFileUploader>
              </v-flex>
              <v-flex xs12 md8 mb-4>
                <TextEditor v-model="screener.description" />
              </v-flex>
            </v-layout>
          </div>

          <v-card-actions>
            <v-btn color="secondary" dark @click="goToStep(4)">
              {{ $t(`${eventPrefix}.btn.continue`) }}
              <v-icon small>$vuetify.icons.screener_next</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </div>
      </v-form>
    </v-stepper-content>
    <v-stepper-step editable :step="4"
      >{{ $t("question_answer.title") }}
      <small>{{ $t("question_answer.desc") }}</small>
    </v-stepper-step>
    <v-stepper-content :step="4">
      <div class="pa-4">
        <v-text-field
          id="qa_info_title"
          :label="$t('title')"
          name="qa_info"
          color="primary"
          prepend-icon="$vuetify.icons.rms_agreement"
          type="text"
          v-model="screener.qa_info.title"
        ></v-text-field>
        <!-- Message -->
        <v-text-field
          id="qa_info_message"
          :label="$t('message')"
          name="qa_info"
          color="primary"
          prepend-icon="$vuetify.icons.rms_agreement"
          type="text"
          v-model="screener.qa_info.message"
        ></v-text-field>
      </div>
      <v-card-actions>
          <v-btn color="secondary" dark @click="goToStep(5)">
            {{ $t(`${eventPrefix}.btn.continue`) }}
            <v-icon small>$vuetify.icons.screener_next</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
    </v-stepper-content>
    <v-stepper-step editable step="5"
      >{{ $t(`${eventPrefix}.save.step.promotion_sponsor_details`) }}
      <small>{{
        $t(`${eventPrefix}.save.step.promotion_sponsor_details.desc`)
      }}</small>
    </v-stepper-step>

    <v-stepper-content step="5">
      <div class="pa-4">
        <v-layout class="pb-3" row wrap>
          <!-- Organization logo -->
          <!-- <v-flex xs12 md3 lg3>
            <div class="subheading greyDarken1--text">
              {{ $t(`${eventPrefix}.save.form.edit_logo`) }}
            </div>
            <v-layout justify-center align-center text-xs-center>
              <v-hover >
                <v-card
                  @click="showDropzoneDialog = true"
                  width="100%"
                  hover
                  slot-scope="{ hover }"
                >
                  <v-img
                    v-if="screener.logo_url"
                    contain
                    height="100px"
                    :src="screener.logo_url"
                  >
                    <v-expand-transition :key="screener.thumbnail_id">
                      <div
                        v-if="hover"
                        class="d-flex transition-fast-in-fast-out error v-card--reveal white--text text-xs-center"
                        style="height: 30%"
                        @click.stop="removeLogo()"
                      >
                        <span class="body-2">Remove</span>
                      </div>
                    </v-expand-transition>
                  </v-img>
                  <v-icon color="blue-grey lighten-4" style="font-size: 70px" v-else>mdi-cloud-upload</v-icon>
                </v-card>
              </v-hover>
            </v-layout>

            <v-dialog max-width="350" persistent v-model="showDropzoneDialog">
              <v-layout wrap justify-end class="white">
                <v-btn
                  small
                  dark
                  icon
                  color="grey"
                  @click.native="showDropzoneDialog = false"
                >
                  <v-icon small>$vuetify.icons.rms_close</v-icon>
                </v-btn>
              </v-layout>

              <vue-dropzone
                ref="myVueDropzone"
                id="dropzone"
                class="pa-0"
                :options="dropzoneOptions"
                :useCustomSlot="true"
                @vdropzone-error="showError"
                @vdropzone-success="successUpload"
                @vdropzone-thumbnail="setThumbnail"
              ></vue-dropzone>
            </v-dialog>
          </v-flex> -->
        </v-layout>
        <div class="subheading greyDarken1--text" v-if="!screener.pre_roll">
          {{ $t(`${eventPrefix}.save.upload.pre_roll`) }}
        </div>
        <VimeoUploader
              @setUrl="setPreRoll"
              v-model="screener.pre_roll"
              label="Upload Preroll"
              previewTitle="Preview of Pre Roll"
              refName="preroll"
            />
        <v-card-actions>
          <v-btn color="secondary" dark @click="goToNextParentStep()">
            {{ $t(`${eventPrefix}.btn.continue`) }}
            <v-icon small>$vuetify.icons.screener_next</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </div>
    </v-stepper-content>
  </v-stepper>
</template>
<script>
import DEFINES from "@/defines";
import rules from "@/validation";
import DateTimePicker from "@/components/DateTimePicker";
import ShowLocalTime from "@/components/ShowLocalTime";
import TextEditor from "@/components/TextEditor";
import { mapState, mapGetters } from "vuex";
import moment from "moment";
import i18n from "@/i18n";
import { buildHeaders } from "../../../api/_manager";
import vue2Dropzone from "@/components/local/vue2-dropzone/src/index.js";
import "@/components/local/vue2-dropzone/dist/vue2Dropzone.min.css";
import SingleFileUploader from "@/components/media/SingleFileUploader";
import AskFilm from "@/components/screener/AskFilm";
import VimeoUploader from "@/components/VimeoUploader";

export default {
  name: "ScreenerCommonSave",
  props: {
    is_vod_event: {
      type: Boolean,
      default: false,
    },
    screener: {
      type: Object,
    },
    eventPrefix: {
      type: String,
      default: "screener",
    },
    screening_type: {
      type: Number,
      default: DEFINES.SCREENING_TYPE.SINGLE_FILM_SCREENING,
    },
    common_stepper_validation: {
      type: Boolean,
      default: true,
    },
    uploaded_thumbnail_id: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      rules,
      commonStepperStep: 1,
      schedule_validation: true,
      registration_schedule_validation: true,
      valid_timelines: [],
      details_validation: true,
      manage_film_validation: true,
      showDropzoneDialog: false,
      showThumbnailUploader: false,
      isDropzoneError: false,
      copy_common_stepper_validation: this.common_stepper_validation,
    };
  },
  components: {
    DateTimePicker,
    ShowLocalTime,
    TextEditor,
    VimeoUploader,
    vueDropzone: vue2Dropzone,
    SingleFileUploader,
    AskFilm
  },
  created() {
    this.getFilms();
  },
  computed: {
    ...mapGetters("company", {
      timezone: "getSelectedCompanyTimezone",
      selected_company_id: "getSelected",
    }),
    ...mapState({
      films: (state) => state.film.selection_list,
      countries: (state) => state.country.list,
    }),
    dropzoneOptions() {
      return {
        url: `${this.DEFINES.BASE_API_URL}/member/company/${this.selected_company_id}/screener/upload_logo`,
        thumbnailWidth: 200,
        maxFilesize: 1,
        chunking: true,
        chunkSize: 2000000, // Bytes
        destroyDropzone: false,
        headers: buildHeaders().headers,
        acceptedFiles: "image/*",
      };
    },
    timelines() {
      return [
        {
          timestamp: this.screener.registration_starts_at,
          string: i18n.t(
            "screener.save.overview.section.screening_timelines.registration_start_time"
          ),
          color: "success",
        },
        {
          timestamp: this.screener.registration_ends_at,
          string: i18n.t(
            "screener.save.overview.section.screening_timelines.registration_end_time"
          ),
          color: "success",
        },
        {
          timestamp: this.screener.screening_starts_at,
          string: i18n.t(
            "screener.save.overview.section.screening_timelines.screening_start_time"
          ),
          color: "primary",
        },
        {
          timestamp: this.screener.screening_ends_at,
          string: i18n.t(
            "screener.save.overview.section.screening_timelines.screening_end_time"
          ),
          color: "primary",
        },
        {
          timestamp: this.screener.screening_tolerance,
          string: i18n.t(
            "screener.save.overview.section.screening_timelines.tolerance_time"
          ),
          color: "error",
        },
      ];
    },
  },
  watch: {
    screener: {
      deep: true,
      handler: function() {
        if(this.screener.event_type == DEFINES.SCREENER_TYPE.PHYSICAL){
          this.screener.screening_tolerance = null;
        }
        this.$emit("update:screener", this.screener);
      },
    },
    copy_common_stepper_validation: {
      handler: function() {
        this.$emit(
          "update:common_stepper_validation",
          this.copy_common_stepper_validation
        );
      },
    },
    uploaded_thumbnail_id: {
      handler: function() {
        this.$emit("update:uploaded_thumbnail_id", this.uploaded_thumbnail_id);
      },
    },
    timelines: {
      deep: true,
      handler: function() {
        this.sortTimelines();
      },
    },
  },
  methods: {
    goToStep(step) {
      this.commonStepperStep = step;
    },
    goToNextParentStep(){
      let step = ( this.screener.event_type == DEFINES.SCREENER_TYPE.PHYSICAL ) ? 3 : 2 ;
      this.$emit("goToStep",step);
    },
    validateSave() {
      if (
        !this.is_vod_event &&
        !(
          this.$refs.schedule_registration.validate()
        )
      ) {
        this.schedule_validation = false;
        this.copy_common_stepper_validation = false;
        this.commonStepperStep = 1;
        return false;
      } else {
        this.schedule_validation = true;
      }

      // Step 2 - Validate Basic Detail
      if (!this.$refs.basic_detail.validate()) {
        this.details_validation = false;
        this.copy_common_stepper_validation = false;
        this.commonStepperStep = 2;
        return false;
      } else {
        this.details_validation = true;
      }

      // Step 3 - Validate Manage Films
      if (!this.$refs.manage_film.validate()) {
        this.manage_film_validation = false;
        this.copy_common_stepper_validation = false;
        this.commonStepperStep = 3;
        return false;
      } else {
        this.manage_film_validation = true;
      }

      this.copy_common_stepper_validation = true;
      return true;
    },

    validateEventSchedule() {
      if (this.is_vod_event) {
        return true;
      }
      // this.screener.screening_starts_at = moment(
      //   this.screener.screening_starts_at
      // ).format("YYYY-MM-DD HH:mm:ss");
      // this.screener.screening_ends_at = moment(
      //   this.screener.screening_ends_at
      // ).format("YYYY-MM-DD HH:mm:ss");
      // this.screener.screening_tolerance = moment(
      //   this.screener.screening_tolerance
      // ).format("YYYY-MM-DD HH:mm:ss");
      if (
        this.screener.screening_starts_at >= this.screener.screening_ends_at
      ) {
        this.copy_common_stepper_validation = false;
        this.$store.dispatch("app/showMessage", {
          message: this.$t(
            `${this.eventPrefix}.save.screening_date_validation`
          ),
          color: "error",
        });
        return false;
      }
      if (
        !this.screener.screening_tolerance ||
        this.screener.screening_starts_at > this.screener.screening_tolerance
      ) {
        this.copy_common_stepper_validation = false;
        this.$store.dispatch("app/showMessage", {
          message: this.$t(`${this.eventPrefix}.save.tolerance_validation`),
          color: "error",
        });
        return false;
      }

      return true;
    },

    dropzoneOptionsForThumbnail(uploading_relative_path, screener_id) {
      return {
        acceptedFiles: "image/*",
        url: `${this.DEFINES.BASE_API_URL}/member/company/${this.selected_company_id}/screener/${uploading_relative_path}`,
        thumbnailWidth: 200,
        maxFilesize: 1, //MB
        chunking: true,
        chunkSize: 2000000, // Bytes
        destroyDropzone: false,
        headers: buildHeaders().headers,
      };
    },
    afterThumbnailUpload(response) {
      if (response.data.media_id) {
        this.uploaded_thumbnail_id = response.data.media_id;
        this.screener.thumbnail_id = response.data.media_id;
      }
    },
    async getFilms() {
      try {
        this.loader = true;
        await this.$store.dispatch("film/getSelectionList");
        this.loader = false;
      } catch (error) {
        this.loader = false;
        this.$store.dispatch("app/showMessage", {
          message: error.message,
          color: "error",
        });
      }
      this.loader = false;
    },

    removeSelectedFilm(film) {
      const index = this.screener.film_ids.indexOf(film.id);
      if (index >= 0) this.screener.film_ids.splice(index, 1);
    },
    validateRegistrationSchedule() {
      this.screener.registration_starts_at = moment(
        this.screener.registration_starts_at
      ).format("YYYY-MM-DD HH:mm:ss");
      this.screener.registration_ends_at = moment(
        this.screener.registration_ends_at
      ).format("YYYY-MM-DD HH:mm:ss");
      this.screener.screening_ends_at = moment(
        this.screener.screening_ends_at
      ).format("YYYY-MM-DD HH:mm:ss");

      if (
        this.screener.registration_starts_at >=
        this.screener.registration_ends_at
      ) {
        this.$store.dispatch("app/showMessage", {
          message: this.$t(
            `${this.eventPrefix}.save.registration_date_validation`
          ),
          color: "error",
        });
        return false;
      }

      if (
        this.screener.screening_ends_at <= this.screener.registration_ends_at
      ) {
        this.$store.dispatch("app/showMessage", {
          message: this.$t(
            `${this.eventPrefix}.save.registration_and_screening_date_validation`
          ),
          color: "error",
        });
        return false;
      }

      return true;
    },
    setScreenerThumbnail(event) {
      if (this.isDropzoneError) {
        this.isDropzoneError = false;
        return;
      }
      this.screener[event.on] = event.value;
    },
    removeThumbnail() {
      this.screener.thumbnail_url = null;
      this.screener.thumbnail_id = null;
    },
    setThumbnail(file, dataUrl) {
      if (this.isDropzoneError) {
        this.isDropzoneError = false;
        return;
      }
      this.screener.logo_url = dataUrl;
    },
    setPreRoll(pre_roll) {
      this.screener.pre_roll = pre_roll;
    },
    removeLogo() {
      this.screener.logo = null;
      this.screener.logo_url = null;
    },
    async showError(event, message = "") {
      try {
        var message = message != "" ? message : event.message;
        this.isDropzoneError = true;
        await this.$store.dispatch("app/showMessage", {
          message: message,
          color: "error",
        });
        this.$refs.myVueDropzone.removeAllFiles();
        this.showDropzoneDialog = false;
      } catch (error) {
        this.$store.dispatch("app/showMessage", {
          message: error.message,
          color: "error",
        });
      }
    },

    successUpload(file, response) {
      this.screener.uploaded_logo = response.data.logo_details;
      this.showDropzoneDialog = false;
      this.$refs.myVueDropzone.removeAllFiles();
    },

    async sortTimelines() {
      this.timelines.sort(function(a, b) {
        return new Date(a.timestamp) - new Date(b.timestamp);
      });
      this.valid_timelines = this.timelines.filter(item => item.timestamp != null);
    },

    isPhysicalEvent(physical_event) {
      if (physical_event) {
        return (
          this.screener.event_type == DEFINES.SCREENER_TYPE.PHYSICAL ||
          this.screener.event_type == DEFINES.SCREENER_TYPE.VIRTUAL_AND_PHYSICAL
        );
      } else {
        return (
          this.screener.event_type != DEFINES.SCREENER_TYPE.PHYSICAL ||
          this.screener.event_type == DEFINES.SCREENER_TYPE.VIRTUAL_AND_PHYSICAL
        );
      }
    },
  },
};
</script>
<style scoped>
#dropzone {
  height: inherit !important;
}
.dropzone {
  min-height: inherit !important;
}
/* Hide inc/dec buttons from input type number */
>>> input[type="number"]::-webkit-inner-spin-button,
>>> input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
>>> input[type="number"] {
  -moz-appearance: textfield;
}
</style>
